<template>
  <div class="swiper-preview-container" :style="containerStyle">
    <!-- 左箭头区域 -->
    <div
      class="arrow arrow-left"
      :class="{ disabled: currentIndex <= 0, placeholder: !showArrows }"
      @click="handlePrev"
    >
      <div v-if="showArrows" class="arrow-icon arrow-icon-left"></div>
    </div>

    <!-- 内容区域 - PC端使用横向滚动 -->
    <div class="swiper-content" :style="swiperStyle" ref="scrollContainer">
      <div class="swiper-track" :style="trackStyle">
        <div
          v-for="(item, index) in list"
          :key="index"
          class="swiper-item-wrapper"
          :style="itemStyle"
        >
          <div
            class="swiper-item"
            :class="{ active: index === activeIndex }"
            @click="handleItemClick(item, index)"
          >
            <slot :item="item" :index="index">
              <img
                v-if="item[imageKey]"
                class="item-image"
                :src="item[imageKey]"
              />
              <div v-else class="default-item">
                <span>{{ index + 1 }}</span>
              </div>
            </slot>
          </div>
        </div>
        <!-- 添加图片按钮 -->
        <div
          v-if="fileType === 'image' && list.length < maxCount"
          class="swiper-item-wrapper"
          :style="itemStyle"
        >
          <div class="add-image-item swiper-item" @click="handleAddImage">
            <div class="add-image-btn">
              <div class="add-icon">+</div>
              <span class="add-text">添加图片</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 右箭头区域 -->
    <div
      class="arrow arrow-right"
      :class="{ disabled: currentIndex >= maxIndex, placeholder: !showArrows }"
      @click="handleNext"
    >
      <div v-if="showArrows" class="arrow-icon arrow-icon-right"></div>
    </div>

    <!-- PC端使用 el-upload 替代 -->
    <el-upload
      ref="uploadRef"
      :action="uploadUrl"
      :headers="uploadHeaders"
      :data="{ type: 'modularImg' }"
      :show-file-list="false"
      :multiple="true"
      :limit="canSelectCount"
      accept="image/*"
      :on-success="onUploadSuccess"
      :on-error="onUploadError"
      :before-upload="beforeUpload"
      :on-exceed="onExceed"
      style="display: none"
    >
    </el-upload>
  </div>
</template>

<script>
import serverConfig from "@/utils/config/index.js";
import { imageUrl } from "@/utils/config/commonUrl.js";
import { generateUUID } from "@/utils/uuid.js";

export default {
  name: "SwiperPreview",
  props: {
    list: {
      type: Array,
      default: () => [],
    },
    height: {
      type: [String, Number],
      default: 200,
    },
    itemWidth: {
      type: [String, Number],
      default: 140,
    },
    itemGap: {
      type: [String, Number],
      default: 16,
    },
    visibleCount: {
      type: Number,
      default: 4,
    },
    activeIndex: {
      type: Number,
      default: -1,
    },
    initialIndex: {
      type: Number,
      default: 0,
    },
    imageKey: {
      type: String,
      default: "url",
    },
    fileType: {
      type: String,
      default: "file",
      validator: (value) => ["file", "image"].includes(value),
    },
    maxCount: {
      type: Number,
      default: 9,
    },
  },
  data() {
    return {
      signFileImg: [],
      currentIndex: 0,
      uploadUrl: `${serverConfig.baseURL}/file/uploadFile`,
      imageUrlBase: imageUrl,
    };
  },
  computed: {
    uploadHeaders() {
      return {
        "Access-Token": localStorage.getItem("token") || "",
      };
    },
    canSelectCount() {
      console.log(this.maxCount, "---this.maxCount");
      console.log(this.list, "this.list--------");

      return this.maxCount - this.list.length;
    },
    containerStyle() {
      return `height: ${this.formatSize(this.height)}`;
    },
    swiperStyle() {
      return `height: ${this.formatSize(this.height)}; overflow: hidden;`;
    },
    trackStyle() {
      const offset = this.currentIndex * (this.itemWidth + this.itemGap);
      return `transform: translateX(-${offset}px); transition: transform 0.3s ease;`;
    },
    itemStyle() {
      return `width: ${this.itemWidth}px; margin-right: ${this.itemGap}px; flex-shrink: 0;`;
    },
    showArrows() {
      // 图片类型时，需要算上添加图片框的数量
      if (this.fileType === "image") {
        const showAddBtn = this.list.length < this.maxCount;
        const totalCount = this.list.length + (showAddBtn ? 1 : 0);
        return totalCount > this.visibleCount;
      }
      return this.list.length > this.visibleCount;
    },
    maxIndex() {
      // 图片类型时，需要算上添加图片框的数量
      let totalCount = this.list.length;
      if (this.fileType === "image" && this.list.length < this.maxCount) {
        totalCount += 1; // 添加图片按钮
      }
      if (totalCount <= this.visibleCount) {
        return 0;
      }
      return totalCount - this.visibleCount;
    },
  },
  watch: {
    initialIndex: {
      handler(val) {
        if (val >= 0 && val <= this.maxIndex) {
          this.currentIndex = val;
        }
      },
      immediate: true,
    },
    list: {
      handler(newVal, oldVal) {
        // 如果是新增图片，滚动到最后显示添加框
        if (oldVal && newVal.length > oldVal.length) {
          // this.$nextTick(() => {
          //   this.scrollToEnd();
          // });
        } else if (this.currentIndex > this.maxIndex) {
          this.currentIndex = Math.max(0, this.maxIndex);
        }
      },
      deep: true,
    },
  },
  methods: {
    // PC端上传前检查
    beforeUpload(file) {
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        this.$message.error("文件不能超过5MB");
        return false;
      }
      return true;
    },
    onExceed(files, fileList) {
      console.log(files, fileList);
      this.$message.error("最多还能上传" + this.canSelectCount + "张图片");
    },
    // PC端上传成功
    onUploadSuccess(response, file) {
      if (response.status === 1) {
        const uploadImageList = [
          {
            pageId: generateUUID(),
            pageIndex: 0,
            imageUrl: this.imageUrlBase + response.msg,
            regions: [],
          },
        ];
        this.$emit("upload-success", uploadImageList);
        this.$refs.uploadRef.clearFiles();
      } else {
        this.$message.error("上传失败");
      }
    },
    // PC端上传失败
    onUploadError(err) {
      console.error("上传失败:", err);
      this.$message.error("上传失败");
    },
    formatSize(size) {
      if (typeof size === "number") {
        return `${size}px`;
      }
      if (typeof size === "string" && /^\d+$/.test(size)) {
        return `${size}px`;
      }
      return size;
    },
    handlePrev() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.$emit("change", this.currentIndex);
      }
    },
    handleNext() {
      if (this.currentIndex < this.maxIndex) {
        this.currentIndex++;
        this.$emit("change", this.currentIndex);
      }
    },
    // PC端不需要 onSwiperChange，通过箭头按钮控制
    scrollToIndex(index) {
      if (index < 0) {
        index = 0;
      } else if (index > this.maxIndex) {
        index = this.maxIndex;
      }
      this.currentIndex = index;
      this.$emit("change", this.currentIndex);
    },
    scrollToItem(index) {
      if (index < this.currentIndex) {
        this.scrollToIndex(index);
      } else if (index >= this.currentIndex + this.visibleCount) {
        this.scrollToIndex(index - this.visibleCount + 1);
      }
    },
    scrollToEnd() {
      // 滚动到最后，让添加图片框可见
      // 总数 = list数量 + 添加框(如果显示)
      const showAddBtn =
        this.fileType === "image" && this.list.length < this.maxCount;
      const totalCount = this.list.length + (showAddBtn ? 1 : 0);
      const newIndex = Math.max(0, totalCount - this.visibleCount);
      this.currentIndex = newIndex;
    },
    handleItemClick(item, index) {
      // 点击时将该图片滚动到可见区域第二个位置（前面留一个可以看到）
      if (this.showArrows) {
        const targetIndex = Math.max(0, Math.min(index - 1, this.maxIndex));
        if (targetIndex !== this.currentIndex) {
          this.currentIndex = targetIndex;
        }
      }
      this.$emit("item-click", { item, index });
    },
    handleAddImage() {
      // PC端触发 el-upload 的文件选择
      this.$refs.uploadRef.$el.querySelector("input").click();
      // if (this.list.length >= this.maxCount) {
      //   uni.showToast({
      //     title: `最多上传${this.maxCount}张图片`,
      //     icon: "none",
      //   });
      //   return;
      // }
      // const remainCount = this.maxCount - this.list.length;
      // uni.chooseImage({
      //   count: remainCount,
      //   sizeType: ["compressed"],
      //   sourceType: ["album", "camera"],
      //   success: (res) => {
      //     this.$emit("choose-image", res.tempFilePaths);
      //   },
      // });
    },
  },
};
</script>

<style lang="scss" scoped>
.swiper-preview-container {
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: 10px 0;
  user-select: none;
}

.arrow {
  width: 30px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;

  .arrow-icon {
    width: 10px;
    height: 10px;
    border-top: 2px solid #999;
    border-right: 2px solid #999;
  }

  .arrow-icon-left {
    transform: rotate(-135deg);
    margin-left: 4px;
  }

  .arrow-icon-right {
    transform: rotate(45deg);
    margin-right: 4px;
  }

  &:hover {
    opacity: 0.7;
  }

  &.disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  &.placeholder {
    pointer-events: none;
  }
}

.swiper-content {
  flex: 1;
  overflow: hidden;
}

.swiper-track {
  display: flex;
  align-items: center;
  height: 100%;
}

.swiper-item-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  box-sizing: border-box;
}

.swiper-item {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  background-color: #fff;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid transparent;
  cursor: pointer;

  &.active {
    border-color: #e99e3e;
  }
}

.item-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.default-item {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #eee;

  span {
    font-size: 16px;
    color: #666;
  }
}

.add-image-item {
  .add-image-btn {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #fff;
    border-radius: 4px;
    border: 1px dashed #ccc;
    box-sizing: border-box;
    cursor: pointer;

    .add-icon {
      font-size: 24px;
      color: #999;
      line-height: 1;
    }

    .add-text {
      font-size: 12px;
      color: #999;
      margin-top: 4px;
    }

    &:hover {
      background-color: #f5f5f5;
    }
  }
}
</style>
